import React, { useRef, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadCloud, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

export default function BulkImportDialog({ open, onOpenChange, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // {summary, preview, errors}
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setBusy(false); if (fileRef.current) fileRef.current.value = ""; };

  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/players/import/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "player-import-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const validate = async (f) => {
    setBusy(true); setPreview(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/players/bulk-import?commit=false", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); setFile(null); }
    finally { setBusy(false); }
  };

  const onPick = (f) => {
    if (!f) return;
    setFile(f);
    validate(f);
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/players/bulk-import?commit=true", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.imported} player${data.imported === 1 ? "" : "s"} imported`);
      reset();
      onOpenChange(false);
      onImported?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const summary = preview?.summary;
  const errors = preview?.errors || [];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#0c1a13] border-white/10 text-white max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-anton text-2xl uppercase tracking-tight">Bulk Import Players</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Upload a CSV or Excel file. We will validate it before anything is saved.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button data-testid="download-template-btn" variant="outline" size="sm"
            onClick={downloadTemplate} className="border-white/20 text-white bg-[#060d09]">
            <Download className="w-4 h-4 mr-1.5" /> Download CSV Template
          </Button>
          <span className="text-xs text-zinc-500">Columns: name, country, category (A-E), rating, is_international, seed_level, registration_fee_paid, achievements, bio, tier</span>
        </div>

        <label className="block">
          <input ref={fileRef} data-testid="bulk-import-file" type="file" accept=".csv,.xlsx,.xlsm"
            className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
          <span className="cursor-pointer flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/15 rounded-lg py-8 hover:border-[#00A86B]/60 hover:bg-[#00A86B]/5 transition-colors">
            {file ? (
              <><FileSpreadsheet className="w-7 h-7 text-[#00A86B]" /><span className="text-sm text-zinc-200">{file.name}</span><span className="text-xs text-zinc-500">Click to choose a different file</span></>
            ) : (
              <><UploadCloud className="w-7 h-7 text-zinc-500" /><span className="text-sm text-zinc-300">Click to upload .csv or .xlsx</span></>
            )}
          </span>
        </label>

        {busy && !preview && <div className="text-center text-zinc-400 text-sm py-2" data-testid="bulk-import-validating">Validating…</div>}

        {summary && (
          <div className="space-y-3" data-testid="bulk-import-preview">
            <div className="flex gap-3 text-sm">
              <span className="flex items-center gap-1.5 text-emerald-400" data-testid="bulk-valid-count">
                <CheckCircle2 className="w-4 h-4" /> {summary.valid} ready
              </span>
              {summary.errors > 0 && (
                <span className="flex items-center gap-1.5 text-amber-400" data-testid="bulk-error-count">
                  <AlertTriangle className="w-4 h-4" /> {summary.errors} with issues (skipped)
                </span>
              )}
              <span className="text-zinc-500">· {summary.total} rows total</span>
            </div>

            {errors.length > 0 && (
              <div className="max-h-40 overflow-auto rounded border border-amber-500/20 bg-amber-500/5 p-2 text-xs">
                {errors.map((er, i) => (
                  <div key={i} className="py-0.5 text-amber-200/90">
                    <X className="w-3 h-3 inline mr-1 text-amber-500" />
                    Row {er.row}{er.name ? ` (${er.name})` : ""}: {er.messages.join("; ")}
                  </div>
                ))}
              </div>
            )}

            {preview.preview?.length > 0 && (
              <div className="max-h-48 overflow-auto rounded border border-white/10">
                <table className="w-full text-xs">
                  <thead className="bg-[#11241b] text-zinc-400 sticky top-0">
                    <tr><th className="text-left px-2 py-1">Name</th><th className="text-left px-2 py-1">Country</th><th className="px-2 py-1">Cat</th><th className="px-2 py-1">Rating</th><th className="px-2 py-1">Seed</th></tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((p, i) => (
                      <tr key={i} className="border-t border-white/5 text-zinc-200">
                        <td className="px-2 py-1">{p.name}</td><td className="px-2 py-1">{p.country}</td>
                        <td className="px-2 py-1 text-center">{p.category}</td><td className="px-2 py-1 text-center">{p.rating}</td><td className="px-2 py-1 text-center">{p.seed_level}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => close(false)} className="border-white/20 text-white">Cancel</Button>
          <Button data-testid="bulk-import-commit-btn" onClick={commit}
            disabled={busy || !summary || summary.valid === 0}
            className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold disabled:opacity-40">
            {busy ? "Importing…" : summary ? `Import ${summary.valid} Player${summary.valid === 1 ? "" : "s"}` : "Import"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
